import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import BRAND from "../brand";
import { ArrowLeft, Users, Building2, LogOut, ShieldCheck, BookOpen } from "lucide-react";
import { UserManagement } from "./admin/UserManagement";
import { TeamManagement } from "./admin/TeamManagement";
import { EditUserDialog } from "./admin/EditUserDialog";
import { UserApprovalPanel } from "./admin/UserApprovalPanel";
import { ScenarioManagement } from "./admin/ScenarioManagement";

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "learner", team_id: "" });
  const [createError, setCreateError] = useState("");
  
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: "", description: "" });
  
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "", team_id: "" });

  const [scenarioData, setScenarioData] = useState({ defaults: [], customs: [] });

  const fetchData = async () => {
    try {
      const [usersRes, teamsRes, scenariosRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/admin/scenarios`)
      ]);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
      setScenarioData(scenariosRes.data);
    } catch (e) {
      console.error("[Admin] Failed to fetch data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateUser = async () => {
    setCreateError("");
    if (!newUser.name || !newUser.email || !newUser.password) {
      setCreateError("Name, email, and password are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/users`, { ...newUser, team_id: newUser.team_id || null });
      setShowCreateUser(false);
      setNewUser({ name: "", email: "", password: "", role: "learner", team_id: "" });
      fetchData();
    } catch (e) {
      setCreateError(e.response?.data?.detail || "Failed to create user");
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeam.name) return;
    try {
      await axios.post(`${API}/teams`, newTeam);
      setShowCreateTeam(false);
      setNewTeam({ name: "", description: "" });
      fetchData();
    } catch (e) {
      setCreateError(e.response?.data?.detail || "Failed to create team");
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, {
        name: editForm.name || undefined,
        role: editForm.role || undefined,
        team_id: editForm.team_id
      });
      setEditingUser(null);
      fetchData();
    } catch (e) {
      setCreateError(e.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      fetchData();
    } catch (e) {
      console.error("[Admin] Failed to delete user:", e);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm("Delete team? Users will be unassigned.")) return;
    try {
      await axios.delete(`${API}/teams/${teamId}`);
      fetchData();
    } catch (e) {
      console.error("[Admin] Failed to delete team:", e);
    }
  };

  const handleAssignManager = async (teamId, managerId) => {
    try {
      await axios.put(`${API}/teams/${teamId}`, { manager_id: managerId });
      fetchData();
    } catch (e) {
      console.error("[Admin] Failed to assign manager:", e);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.team_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const managers = users.filter(u => u.role === "manager" || u.role === "admin");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-[#2563EB] rounded-full animate-spin"></div>
          <p className="text-slate-500">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-slate-500 hover:text-[#0B1F3B]"><ArrowLeft className="w-5 h-5" /></Button>
              </Link>
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-7 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              <div>
                <h1 className="text-lg font-bold text-[#0B1F3B]">Admin Panel</h1>
                <p className="text-xs text-slate-400">{BRAND.productName}</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Dashboard</Link>
              <Link to="/simulator" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Practice</Link>
              <Link to="/analytics" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Team</Link>
              <Link to="/admin" className="text-sm font-medium text-[#0B1F3B]">Admin</Link>
            </nav>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-[#0B1F3B]">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} className="text-slate-500 hover:text-[#0B1F3B]" data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-6">
            <TabsList className="bg-white border border-slate-200/60 rounded-xl p-1">
              <TabsTrigger value="users" data-testid="users-tab" className="rounded-lg data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" /> Users ({users.length})
              </TabsTrigger>
              <TabsTrigger value="teams" data-testid="teams-tab" className="rounded-lg data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">
                <Building2 className="w-4 h-4 mr-2" /> Teams ({teams.length})
              </TabsTrigger>
              <TabsTrigger value="approvals" data-testid="approvals-tab" className="rounded-lg data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">
                <ShieldCheck className="w-4 h-4 mr-2" /> Approvals
                {users.filter(u => u.status === "pending_approval").length > 0 && (
                  <span className="ml-1.5 bg-amber-400 text-[#0B1F3B] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {users.filter(u => u.status === "pending_approval").length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="scenarios" data-testid="scenarios-tab" className="rounded-lg data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">
                <BookOpen className="w-4 h-4 mr-2" /> Scenarios ({scenarioData.defaults.length + scenarioData.customs.length})
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="users">
            <UserManagement
              filteredUsers={filteredUsers} searchQuery={searchQuery} setSearchQuery={setSearchQuery}
              showCreateUser={showCreateUser} setShowCreateUser={setShowCreateUser}
              newUser={newUser} setNewUser={setNewUser} createError={createError}
              handleCreateUser={handleCreateUser} teams={teams}
              handleDeleteUser={handleDeleteUser} setEditingUser={setEditingUser}
              setEditForm={setEditForm} currentUserId={user?.id}
            />
          </TabsContent>

          <TabsContent value="teams">
            <TeamManagement
              teams={teams} managers={managers}
              showCreateTeam={showCreateTeam} setShowCreateTeam={setShowCreateTeam}
              newTeam={newTeam} setNewTeam={setNewTeam}
              handleCreateTeam={handleCreateTeam} handleDeleteTeam={handleDeleteTeam}
              handleAssignManager={handleAssignManager}
            />
          </TabsContent>

          <TabsContent value="approvals">
            <UserApprovalPanel users={users} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="scenarios">
            <ScenarioManagement defaults={scenarioData.defaults} customs={scenarioData.customs} onRefresh={fetchData} />
          </TabsContent>
        </Tabs>
      </main>

      <EditUserDialog
        editingUser={editingUser} setEditingUser={setEditingUser}
        editForm={editForm} setEditForm={setEditForm}
        handleUpdateUser={handleUpdateUser} teams={teams}
      />

      <footer className="border-t border-slate-200/60 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-sm text-slate-400">{BRAND.productName}</span>
            <div className="text-center sm:text-right">
              <p className="text-xs text-slate-400">{BRAND.poweredBy}</p>
              <p className="text-xs text-slate-300">{BRAND.companyAttribution}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
